"use client";

import { useState } from "react";
import { Loader2, Sparkles, Undo2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { aiServiceFetch } from "@/lib/ai-service-client";

type PromptEnhancementResponse = {
  enhancedPrompt: string | null;
  reason?: string;
};

export type EnhancePromptButtonProps = {
  id?: string;
  prompt: string;
  onComplete?: (result: PromptEnhancementResponse) => void;
  onError?: (message: string) => void;
  className?: string;
  label?: string;
  disabled?: boolean;
  minCharacters?: number;
  mode?: "enhance" | "undo";
  onUndo?: () => void;
};

export function EnhancePromptButton({
  id = "enhance-prompt-button",
  prompt,
  onComplete,
  onError,
  className,
  disabled = false,
  minCharacters = 12,
  mode = "enhance",
  onUndo,
  label,
}: EnhancePromptButtonProps) {
  const [isEnhancing, setIsEnhancing] = useState(false);

  const isUndo = mode === "undo";
  const resolvedLabel =
    label ?? (isUndo ? "Undo enhancement" : "Enhance prompt");

  const handleClick = async () => {
    if (disabled || isEnhancing) {
      return;
    }

    if (isUndo) {
      onUndo?.();
      return;
    }

    const trimmed = prompt.trim();
    const wordCount = trimmed ? trimmed.split(/\s+/).length : 0;
    if (trimmed.length < minCharacters || wordCount < 3) {
      onError?.("Add a bit more detail before enhancing.");
      return;
    }

    try {
      setIsEnhancing(true);

      const response = await aiServiceFetch(`/api/prompt-enhancement`, {
        method: "POST",
        body: JSON.stringify({ prompt: trimmed }),
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(
          payload.error ||
            `Failed to enhance prompt (status ${response.status})`,
        );
      }

      const result = (await response.json()) as PromptEnhancementResponse;
      onComplete?.({
        enhancedPrompt: result.enhancedPrompt ?? null,
        reason: result.reason,
      });
    } catch (error) {
      onError?.(
        error instanceof Error ? error.message : "Failed to enhance prompt.",
      );
    } finally {
      setIsEnhancing(false);
    }
  };

  const trimmed = prompt.trim();
  const wordCount = trimmed ? trimmed.split(/\s+/).length : 0;
  const isEligible =
    isUndo || (trimmed.length >= minCharacters && wordCount >= 3);
  const isBusy = disabled || (isEnhancing && !isUndo);

  return (
    <button
      id={id}
      type="button"
      aria-busy={isEnhancing}
      aria-disabled={isBusy || !isEligible}
      onClick={handleClick}
      disabled={isBusy}
      className={cn(
        "inline-flex cursor-pointer h-6 items-center gap-1.5 rounded-md border border-border/60 bg-background/80 px-2.5 text-left text-[11px] font-normal text-foreground/65 shadow-sm backdrop-blur transition-colors relative overflow-hidden",
        "before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/[0.04] before:to-white/[0.01] before:opacity-0 before:transition-opacity",
        isBusy && "cursor-not-allowed opacity-60",
        !isBusy &&
          (isUndo
            ? "hover:border-foreground/20 hover:text-foreground [&:hover]:before:opacity-100"
            : isEligible
              ? "hover:border-foreground/20 hover:text-foreground [&:hover]:before:opacity-100"
              : "border-border text-foreground/45"),
        className,
      )}
    >
      {isUndo ? (
        <Undo2 className="size-3 text-muted-foreground transition-colors relative z-10" />
      ) : isEnhancing ? (
        <Loader2 className="size-3 animate-spin text-muted-foreground relative z-10" />
      ) : (
        <Sparkles className="size-3 text-muted-foreground transition-colors relative z-10" />
      )}
      <span className="relative z-10">
        {isUndo ? resolvedLabel : isEnhancing ? "Enhancing..." : resolvedLabel}
      </span>
    </button>
  );
}
